// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect, Dispatch } from 'react-redux'; // eslint-disable-line no-unused-vars
import { decamelizeKeys } from 'humps';
import api from 'utils/api';
import {
  referralDashboardPageMounted,
  referralDashboardNextPageRequested,
  outOfGroupReasonRequired,
  outOfGroupReasonProvided,
} from 'shared/actions/ReferralProfiles/ReferralDashboardActions';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
/* eslint-disable no-unused-vars */
import {
  ReferralConfirmationUrlParams,
  OutOfGroupFormType,
  PatientChooseFormType,
  ReferralProfile,
  ProviderMappingResult,
  Location,
  AppointmentSlot,
  Network,
  Specialty,
} from './types';
/* eslint-enable no-unused-vars */

/** @typedef {{
  onSlotClick: (slot: AppointmentSlot) => any,
  onProviderClick: (result: ProviderMappingResult) => any,
  onLocationClick: (location: Location) => any,
  onOrganizationClick: (organizationId: string) => any,
  onLoadMore: () => any,
  onPatientChooseSubmit: (formValues: PatientChooseFormType) => Promise,
  onOutOfGroupReasonSubmit: (formValues: OutOfGroupFormType) => any,
  referralProfile?: ReferralProfile,
  results?: ProviderMappingResult[],
  currentPage: number,
  totalPages: number,
  loaded: boolean,
  isFetching: boolean,
  network: Network,
  referrableSpecialties?: Specialty[],
  filterByDistance: boolean,
}} ReferralDashboardManagerRenderPropPropsType */

/** @typedef {{
  referralConfirmationUrl: (params: ReferralConfirmationUrlParams) => {pathname: string, query: JsonGETParams} | string,
  referralProfileId: string,
  history: any,
  render: (props: ReferralDashboardManagerRenderPropPropsType) => JSX.Element,
  requestedNetworkId: string
}} WrapperPropsType */

const wrapperPropTypes = {
  referralConfirmationUrl: PropTypes.func.isRequired,
  referralProfileId: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  render: PropTypes.func.isRequired,
  requestedNetworkId: PropTypes.string,
};

/** @type { React.FC<WrapperPropsType> } */
const Wrapper = ({
  referralConfirmationUrl,
  referralProfileId,
  history,
  render,
  requestedNetworkId,
}) => (
  <ConnectedReferralDashboardManager
    referralConfirmationUrl={referralConfirmationUrl}
    referralProfileId={referralProfileId}
    history={history}
    render={render}
    requestedNetworkId={requestedNetworkId}
  />
);

Wrapper.displayName = 'Wrapper(ReferralDashboardManager)';
Wrapper.propTypes = wrapperPropTypes;

export default Wrapper;

/** @typedef {{
  render: (props: ReferralDashboardManagerRenderPropPropsType) => JSX.Element,
  referralConfirmationUrl: (params: ReferralConfirmationUrlParams) => {pathname: string, query: JsonGETParams},
  referralProfileId: string,
  dispatch: Dispatch<any>,
  apiPrefix: string,
  webpageBase?: string,
  specialtyId?: string,
  history: any,
  referralProfile?: ReferralProfile,
  results?: ProviderMappingResult[],
  currentPage: number,
  totalPages: number,
  loaded: boolean,
  isFetching: boolean
}} ReferralDashboardManagerPropsType */

/** @type { React.ValidationMap<ReferralDashboardManagerPropsType> } */
const propTypes = {
  referralProfileId: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  apiPrefix: PropTypes.string.isRequired,
  webpageBase: PropTypes.string,
  specialtyId: PropTypes.string,
  history: PropTypes.object.isRequired,
  referralProfile: PropTypes.object,
  results: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loaded: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
};

/** @augments { Component<ReferralDashboardManagerPropsType, {}> } */
class ReferralDashboardManager extends Component {
  constructor(props) {
    super(props);
    // Some "link" click handlers
    this.onSlotClick = this.onSlotClick.bind(this);
    this.onProviderClick = this.onProviderClick.bind(this);
    this.onLocationClick = this.onLocationClick.bind(this);
    this.onOrganizationClick = this.onOrganizationClick.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);

    // Handle a couple modal form submissions
    this.onPatientChooseSubmit = this.onPatientChooseSubmit.bind(this);
    this.onOutOfGroupReasonSubmit = this.onOutOfGroupReasonSubmit.bind(this);
  }

  componentDidMount() {
    const { referralProfileId, dispatch, requestedNetworkId } = this.props;
    dispatch(
      referralDashboardPageMounted(referralProfileId, requestedNetworkId)
    );
  }

  /**
   * On Slot Click, grab the ProviderMappingResult for that slot and then
   * either show the "out of group" modal, or navigate to the "new booked referral" page
   *
   * @param {AppointmentSlot} slot
   */
  onSlotClick(slot) {
    const {
      webpageBase,
      specialtyId,
      results,
      referralProfile,
      dispatch,
      history,
      referralConfirmationUrl,
    } = this.props;
    const result = results.find((r) => r.id === slot.providerMappingId);
    const url = referralConfirmationUrl({
      webpageBase,
      referralProfileId: referralProfile.id,
      specialtyId,
      appointmentSlotId: slot.id,
      providerMappingId: result.id,
    });

    if (referralProfile.hasLoyaltyGroup && !result.provider.isInGroup) {
      dispatch(outOfGroupReasonRequired(() => history.push(url)));
    } else {
      history.push(url);
    }
  }

  /**
   * On ProviderMappingResult click, either show the "out of group" modal, or
   * navigate to the "new unbooked referral" page for the clicked provider/location combo.
   *
   * @param {ProviderMappingResult} result
   */
  onProviderClick(result) {
    const {
      webpageBase,
      specialtyId,
      referralProfile,
      dispatch,
      history,
      referralConfirmationUrl,
    } = this.props;
    const url = referralConfirmationUrl({
      webpageBase,
      referralProfileId: referralProfile.id,
      specialtyId,
      providerMappingId: result.id,
    });

    if (
      !referralProfile.enteredByGuest &&
      !referralProfile.enteredByReceiver &&
      referralProfile.hasLoyaltyGroup &&
      !result.provider.isInGroup
    ) {
      dispatch(outOfGroupReasonRequired(() => history.push(url)));
    } else {
      history.push(url);
    }
  }

  /**
   * On Location click, either show the "out of group" modal (TODO), or
   * navigate to the "new unassigned referral" page for the clicked location.
   *
   * @param {Location} location
   */
  onLocationClick(location) {
    const {
      webpageBase,
      referralProfileId,
      specialtyId,
      referralConfirmationUrl,
    } = this.props;
    const url = referralConfirmationUrl({
      webpageBase,
      referralProfileId,
      specialtyId,
      locationId: location.id,
    });
    /** @todo plug hole allowing folks to refer out of group like this */
    this.props.history.push(url);
  }

  /**
   * On "Refer to Organization" click (only possible for organizations with `accept_organization_referrals = true`),
   * either show the "out of group" modal (TODO), or navigate to the "new organization referral" page for
   * the clicked organization, which is probably the subdomain organization.
   *
   * @param {string} organizationId
   */
  onOrganizationClick(organizationId) {
    const {
      webpageBase,
      referralProfileId,
      specialtyId,
      referralConfirmationUrl,
    } = this.props;
    const url = referralConfirmationUrl({
      webpageBase,
      referralProfileId,
      specialtyId,
      organizationId,
    });
    /** @todo consider how this relates to in/out of group referrals */
    this.props.history.push(url);
  }

  /**
   * On submit handler for the "send unassigned" AKA "patient choose" modal form.
   * Creates referral, flashes success, and navigates to referral#show.
   *
   * @param {{patientEmail?: string, formattedPrimaryPhone: string}} formValues
   * @returns {Promise}
   */
  onPatientChooseSubmit(formValues) {
    const {
      apiPrefix,
      webpageBase,
      referralProfile,
      specialtyId,
      history,
    } = this.props;
    return api
      .post(
        `${apiPrefix}/referral_profiles/${referralProfile.id}/unclaimed_referrals`,
        {
          referral_attributes: decamelizeKeys(formValues),
          specialty_id: specialtyId,
        }
      )
      .then((response) => {
        FlashMessageActions.flashSuccess({
          message: 'You have successfully created a referral',
        });
        history.push(`${webpageBase}/referrals/${response.data.id}`);
      });
  }

  /**
   * @param {{reason: string}} formValues
   */
  onOutOfGroupReasonSubmit(formValues) {
    const { reason } = formValues;
    const { dispatch } = this.props;
    return dispatch(outOfGroupReasonProvided(reason));
  }

  onLoadMore() {
    const { dispatch, referralProfileId, requestedNetworkId } = this.props;
    dispatch(
      referralDashboardNextPageRequested(referralProfileId, requestedNetworkId)
    );
  }

  render() {
    const {
      referralProfile,
      results,
      currentPage,
      totalPages,
      loaded,
      isFetching,
      render,
      network,
      organizationReferralRejected,
    } = this.props;

    return render({
      onSlotClick: this.onSlotClick,
      onProviderClick: this.onProviderClick,
      onLocationClick: this.onLocationClick,
      onOrganizationClick: this.onOrganizationClick,
      onLoadMore: this.onLoadMore,
      onPatientChooseSubmit: this.onPatientChooseSubmit,
      onOutOfGroupReasonSubmit: this.onOutOfGroupReasonSubmit,
      referralProfile,
      results,
      currentPage,
      totalPages,
      loaded,
      isFetching,
      referrableSpecialties: network ? network.referrableSpecialties : null,
      filterByDistance: network ? network.filterByDistance : false,
      network,
      organizationReferralRejected
    });
  }
}

ReferralDashboardManager.propTypes = propTypes;

function mapStateToProps(state, ownProps) {
  const { pageState, entities } = state.referralDashboardResults;
  const { referralProfileId } = ownProps;
  const results = pageState.currentResults.map((id) => {
    const matchingResult = entities.matchingResults[id];
    return {
      ...matchingResult,
      location: entities.locations[matchingResult.location],
      provider: entities.providers[matchingResult.provider],
    };
  });

  return {
    isFetching: pageState.isFetching,
    currentPage: pageState.currentPage,
    totalPages: pageState.totalPages,
    specialtyId: pageState.specialtyId,
    network: pageState.network,
    organizationReferralRejected: pageState.organizationReferralRejected,
    apiPrefix: state.configuration.apiPrefix,
    webpageBase: state.configuration.webpageBase,
    referralProfile: entities.referralProfiles[referralProfileId],
    results,
    loaded:
      pageState.lastUpdated !== null &&
      !!entities.referralProfiles[referralProfileId],
  };
}

const ConnectedReferralDashboardManager = connect(mapStateToProps)(
  ReferralDashboardManager
);
